import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { daysAgoISODate, todayISODate } from "@/stores/filterStore";
import SectionRangePicker from "@/components/SectionRangePicker";
import ToggleGroup from "@/components/ToggleGroup";
import Panel from "./Panel";
import Skeleton from "./Skeleton";
import ScenarioSegment from "./finops/ScenarioSegment";
import BoardSegment from "./finops/BoardSegment";
import CacheSegment from "./finops/CacheSegment";
import RulesSegment from "./finops/RulesSegment";

// FinOps 탭 오케스트레이터 (2026-07-20 분해 — 964줄 단일 파일에서 세그먼트별
// 파일로. 계기는 훅 순서 사고 React #310). 여기는 얇게: 기간·세그먼트 상태
// (URL ?seg= 동기화)와 공용 쿼리(현재+직전 기간)만 소유하고, 내용은 전부
// finops/ 하위 세그먼트 컴포넌트가 맡는다. 규칙: 훅은 전부 early return 이전에.
const SEGMENTS = [
  { id: "story", label: "경영 시나리오" },
  { id: "board", label: "계기판" },
  { id: "cache", label: "캐시 효율" },
  { id: "rules", label: "정책·규칙" },
];

const SEGMENT_DESC = {
  story: "경영 보고용 — 조건을 바꿔가며 직접비 기여이익을 시뮬레이션합니다 (근거는 이 기간 실측)",
  board: "매일 아침 상태 배너만 10초 — 나머지는 필요할 때 펼쳐 봅니다",
  cache: "캐싱 투자가 얼마나 아끼는지 — 회색 막대(사용량) 위 선(히트율)으로 읽습니다",
  rules: "가격 정책의 단일 참조점 — 정책을 바꾸면 여기부터 갱신합니다",
};

function defaultRange() {
  return { from: daysAgoISODate(13), to: todayISODate() };
}

function shiftISO(dateStr, days) {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function FinopsSection() {
  const [range, setRange] = useState(defaultRange);

  // 세그먼트를 URL(?seg=)에 반영 — 새로고침 유지 + 딥링크(?tab=finops&seg=story)
  const [searchParams, setSearchParams] = useSearchParams();
  const segParam = searchParams.get("seg");
  const seg = SEGMENTS.some((x) => x.id === segParam) ? segParam : "story";
  const setSeg = (id) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("seg", id);
    return next;
  }, { replace: true });

  const q = useAnalyticsQuery(
    (p, s) => analyticsApi.finops({ from: range.from, to: range.to }, s),
    [range.from, range.to], range);

  // 직전 동일 길이 기간 — 계기판 KPI 델타 배지용
  const spanDays = Math.round((new Date(range.to) - new Date(range.from)) / 86400000) + 1;
  const prevRange = { from: shiftISO(range.from, -spanDays), to: shiftISO(range.to, -spanDays) };
  const qPrev = useAnalyticsQuery(
    (p, s) => analyticsApi.finops({ from: prevRange.from, to: prevRange.to }, s),
    [prevRange.from, prevRange.to], prevRange);

  if (q.loading && !q.data) return <Skeleton />;
  if (q.error) return <Panel title="FinOps"><p className="text-sm text-red-600">불러오기 실패 — {String(q.error.message || q.error)}</p></Panel>;
  const d = q.data || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm text-ink-500">{SEGMENT_DESC[seg]}</p>
          <p className="mt-0.5 text-[12.5px] font-medium text-ink-700">
            조회 기간: {range.from} ~ {range.to} ({spanDays}일)
            {seg === "board" && (
              <span className="ml-2 font-normal text-ink-400">증감 배지는 직전 {spanDays}일({prevRange.from} ~ {prevRange.to}) 대비</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ToggleGroup options={SEGMENTS} value={seg} onChange={setSeg} />
          <SectionRangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
        </div>
      </div>

      {seg === "story" && <ScenarioSegment d={d} />}
      {seg === "board" && <BoardSegment d={d} pv={qPrev.data?.totals || null} range={range} spanDays={spanDays} />}
      {seg === "cache" && <CacheSegment d={d} range={range} />}
      {seg === "rules" && <RulesSegment d={d} />}
    </div>
  );
}
